import { __ } from '@wordpress/i18n';
import PropTypes from 'prop-types';
import React from 'react';
import { useNavigate } from 'react-router-dom';

// Components.
import FilterGroup from '@/components/filter-group';
import Radio from '@/components/radio';
import Select from '@/components/select';

// Config.
import { FILTERS_SHAPE } from '@/config/prop-types';
import yearTypes from '@/config/year-types';

// Services.
import getFilterUrl from '@/services/filters/get-filter-url';
import getServiceTypeOptions from '@/services/filters/get-service-type-options';
import getSourceOfFundsOptions from '@/services/filters/get-source-of-funds-options';
import getYearOptions from '@/services/filters/get-year-options';
import modifyFilters from '@/services/filters/modify-filters';

const Filters = ({
  definitions,
  filters,
  maxYear,
  minYear,
}) => {
  const navigate = useNavigate();

  // Determine whether the first filter group is checkboxes.
  const checkboxesFirst = filters.tab % 2 === 0;

  // Get the options for years based on min/max.
  const yearOptions = getYearOptions(minYear, maxYear);

  /**
   * A function that returns a function that handles changes to a filter group.
   * Parameters allow the resulting function to be customized for the filter
   * key and the type of filter (replace vs. toggle).
   * @param {string} filter - The filter key to modify.
   * @param {string} type - The type of modification to make. One of 'toggle', 'set'.
   * @returns {function} A function that handles the change event.
   */
  const handleFilterGroupChange = (filter, type) => (next) => navigate(
    getFilterUrl(
      (type === 'set')
        ? modifyFilters(filters, 'set', filter, [next])
        : modifyFilters(filters, 'toggle', filter, next),
    ),
  );

  /**
   * A function that handles a year filter type change.
   * @param {string} next - The next value for the radio button.
   */
  const handleYearTypeChange = (next) => navigate(getFilterUrl(modifyFilters(filters, 'set', 'yearType', next)));

  return (
    <aside className="hst-filter">
      <h2 className="hst-filter__label">{__('Filter By', 'hst')}</h2>
      <FilterGroup
        helpText={__('For more information on these categories, see the Definitions PDF below.', 'hst')}
        limit={6}
        onChange={handleFilterGroupChange('serviceType', checkboxesFirst ? 'toggle' : 'set')}
        options={getServiceTypeOptions(filters.tab)}
        title={__('Service Type', 'hst')}
        type={checkboxesFirst ? 'checkbox' : 'radio'}
        values={filters.serviceType}
      />
      <FilterGroup
        helpText={__('For more information on these categories, see the Definitions PDF below.', 'hst')}
        limit={3}
        onChange={handleFilterGroupChange('sourceOfFunds', checkboxesFirst ? 'set' : 'toggle')}
        options={getSourceOfFundsOptions(filters.tab)}
        title={__('Source of Funds', 'hst')}
        type={checkboxesFirst ? 'radio' : 'checkbox'}
        values={filters.sourceOfFunds}
      />
      <h4 className="hst-label__year">{__('Year', 'hst')}</h4>
      <Radio
        checked={filters.yearType === 'single'}
        label={yearTypes.get('single')}
        name="yearType"
        onChange={handleYearTypeChange}
        value="single"
      />
      <Select
        hiddenLabel
        label={__('Select single year', 'hst')}
        onChange={(next) => navigate(getFilterUrl(modifyFilters(filters, 'set', 'yearSingle', next)))}
        options={yearOptions}
        value={filters.yearSingle === '*' ? maxYear.toString() : filters.yearSingle.toString()}
      />
      <Radio
        checked={filters.yearType === 'compare'}
        label={yearTypes.get('compare')}
        name="yearType"
        onChange={handleYearTypeChange}
        value="compare"
      />
      <Select
        hiddenLabel
        label={__('Select first year to compare', 'hst')}
        onChange={(next) => navigate(getFilterUrl(modifyFilters(filters, 'set', 'yearCompare', [next, filters.yearCompare[1]])))}
        options={yearOptions}
        value={filters.yearCompare[0] === '*' ? minYear.toString() : filters.yearCompare[0].toString()}
      />
      <Select
        hiddenLabel
        label={__('Select second year to compare', 'hst')}
        onChange={(next) => navigate(getFilterUrl(modifyFilters(filters, 'set', 'yearCompare', [filters.yearCompare[0], next])))}
        options={yearOptions}
        value={filters.yearCompare[1] === '*' ? maxYear.toString() : filters.yearCompare[1].toString()}
      />
      <Radio
        checked={filters.yearType === 'range'}
        label={yearTypes.get('range')}
        name="yearType"
        onChange={handleYearTypeChange}
        value="range"
      />
      <Select
        hiddenLabel
        label={__('Select lower end of year range', 'hst')}
        onChange={(next) => navigate(getFilterUrl(modifyFilters(filters, 'set', 'yearRange', [next, filters.yearRange[1]])))}
        options={yearOptions}
        value={filters.yearRange[0] === '*' ? minYear.toString() : filters.yearRange[0].toString()}
      />
      <Select
        hiddenLabel
        label={__('Select upper end of year range', 'hst')}
        onChange={(next) => navigate(getFilterUrl(modifyFilters(filters, 'set', 'yearRange', [filters.yearRange[0], next])))}
        options={yearOptions}
        value={filters.yearRange[1] === '*' ? maxYear.toString() : filters.yearRange[1].toString()}
      />
      {definitions ? (
        <footer className="hst-filter__subset hst-filter__subset--footer">
          <h3>{__('Definitions', 'hst')}</h3>
          <p><a href={definitions}>{__('Download PDF', 'hst')}</a></p>
        </footer>
      ) : null}
    </aside>
  );
};

Filters.propTypes = {
  definitions: PropTypes.string.isRequired,
  filters: PropTypes.shape(FILTERS_SHAPE).isRequired,
  maxYear: PropTypes.number.isRequired,
  minYear: PropTypes.number.isRequired,
};

export default Filters;
